/**
 * Virtual Clock Store
 * ------------------------------------------------------------
 * Single source of truth for simulated time across the platform.
 * The DaySimulator reads/writes here; UI components subscribe.
 *
 * Speeds:
 *   0  = paused
 *   1  = real-time (1 virtual sec per real sec)
 *  10  = 10x
 *  60  = 1 virtual minute per real second
 * 600  = 10 virtual minutes per real second (timelapse)
 */
import { create } from 'zustand';

export type SimSpeed = 0 | 1 | 10 | 60 | 600;

interface VirtualClockState {
  virtualTime: Date;
  speed: SimSpeed;
  startedAt: Date;
  tickCount: number;

  setSpeed: (s: SimSpeed) => void;
  pause: () => void;
  resume: () => void;
  jumpTo: (d: Date) => void;
  resetToShiftStart: () => void;
  /** Called by heartbeat: advance N virtual ms */
  tick: (deltaMs: number) => void;
}

function shiftStart(): Date {
  // Shift A starts at 06:00 of today
  const d = new Date();
  d.setHours(6, 0, 0, 0);
  return d;
}

export const useVirtualClock = create<VirtualClockState>((set) => ({
  virtualTime: shiftStart(),
  speed: 1,
  startedAt: new Date(),
  tickCount: 0,

  setSpeed: (s) => set({ speed: s }),
  pause: () => set({ speed: 0 }),
  resume: () => set((state) => ({ speed: state.speed === 0 ? 1 : state.speed })),
  jumpTo: (d) => set({ virtualTime: new Date(d) }),
  resetToShiftStart: () => set({ virtualTime: shiftStart(), tickCount: 0 }),
  tick: (deltaMs) =>
    set((state) => ({
      virtualTime: new Date(state.virtualTime.getTime() + deltaMs),
      tickCount: state.tickCount + 1,
    })),
}));
